import { create } from 'zustand';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Configure axios defaults
const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' }
});

// Request interceptor for auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('capitalops_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor for token refresh + credit/AI exhaustion handling
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;
    const detail = error.response?.data?.detail;

    if (status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        const refreshToken = localStorage.getItem('capitalops_refresh_token');
        const { data } = await axios.post(`${API_URL}/api/auth/refresh`, { refreshToken });
        localStorage.setItem('capitalops_token', data.accessToken);
        localStorage.setItem('capitalops_refresh_token', data.refreshToken);
        originalRequest.headers.Authorization = `Bearer ${data.accessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        localStorage.removeItem('capitalops_token');
        localStorage.removeItem('capitalops_refresh_token');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    // Credit / AI exhaustion (402 Payment Required) — surface global event
    if (status === 402) {
      const evtName = detail === 'ai_calls_exhausted' ? 'luminex:ai-exhausted' : 'luminex:credits-exhausted';
      try { window.dispatchEvent(new CustomEvent(evtName, { detail: { error } })); } catch (dispatchErr) { console.warn('[authStore] dispatchEvent failed:', dispatchErr); }
    }
    return Promise.reject(error);
  }
);

export const useAuthStore = create((set, get) => ({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,

  // Initialize from stored token
  init: async () => {
    const token = localStorage.getItem('capitalops_token');
    if (!token) {
      set({ isLoading: false, isAuthenticated: false });
      return;
    }
    try {
      const { data } = await api.get('/api/auth/me');
      set({ user: data.user, isAuthenticated: true, isLoading: false });
    } catch (err) {
      localStorage.removeItem('capitalops_token');
      localStorage.removeItem('capitalops_refresh_token');
      set({ user: null, isAuthenticated: false, isLoading: false });
    }
  },

  // Register
  register: async (formData) => {
    try {
      set({ error: null });
      const { data } = await api.post('/api/auth/register', formData);
      localStorage.setItem('capitalops_token', data.accessToken);
      localStorage.setItem('capitalops_refresh_token', data.refreshToken);
      set({ user: data.user, isAuthenticated: true });
      return data;
    } catch (err) {
      const msg = err.response?.data?.detail || err.response?.data?.error || 'Registration failed';
      set({ error: msg });
      throw new Error(msg);
    }
  },

  // Login
  login: async (email, password) => {
    try {
      set({ error: null });
      const { data } = await api.post('/api/auth/login', { email, password });
      localStorage.setItem('capitalops_token', data.accessToken);
      localStorage.setItem('capitalops_refresh_token', data.refreshToken);
      set({ user: data.user, isAuthenticated: true });
      return data;
    } catch (err) {
      const msg = err.response?.data?.detail || err.response?.data?.error || 'Login failed';
      set({ error: msg });
      throw new Error(msg);
    }
  },

  // Logout
  logout: async () => {
    try {
      await api.post('/api/auth/logout');
    } catch (e) {
      // Logout failure is non-fatal — we still clear the local session below.
      // Logged for observability so a backend-side outage shows up in console.
      console.warn('[authStore] /api/auth/logout failed (clearing local session anyway):', e?.response?.status || e?.message);
    }
    localStorage.removeItem('capitalops_token');
    localStorage.removeItem('capitalops_refresh_token');
    set({ user: null, isAuthenticated: false });
  },

  // Refresh user data
  refreshUser: async () => {
    try {
      const { data } = await api.get('/api/auth/me');
      set({ user: data.user });
    } catch (err) {
      // Background refresh failure is non-fatal — keep current cached user.
      console.warn('[authStore] refreshUser failed:', err?.response?.status || err?.message);
    }
  },

  // Update tokens locally
  updateTokens: (tokens) => {
    const user = get().user;
    if (user) set({ user: { ...user, tokens } });
  },

  clearError: () => set({ error: null }),
}));

// API Functions
export const authAPI = {
  register: (data) => api.post('/api/auth/register', data),
  login: (data) => api.post('/api/auth/login', data),
  getMe: () => api.get('/api/auth/me'),
  logout: () => api.post('/api/auth/logout'),
  updateProfile: (data) => api.put('/api/users/profile', data),
  changePassword: (data) => api.post('/api/auth/change-password', data),
};

export const libraryAPI = {
  browse: (params) => api.get('/api/library', { params }),
  download: (id) => api.post(`/api/library/${id}/download`),
  getTemplate: (id) => api.get(`/api/library/${id}/template`),
};

export const lomAPI = {
  getIndustries: () => api.get('/api/lom/industries'),
  getLom: (subIndustryId) => api.get(`/api/lom/${subIndustryId}`),
  // Free-plan L4 view quota (2 lifetime views on free plan)
  getViewQuota: () => api.get('/api/lom/view-quota'),
  recordL4View: (bpId, title, breadcrumb) => api.post('/api/lom/view-l4', { bpId, title, breadcrumb }),
  getUpstreamLom: () => api.get('/api/lom/upstream'),
  getCrmLom4Tier: () => api.get('/api/lom/crm-4tier'),
  getHcmLom4Tier: () => api.get('/api/lom/hcm-4tier'),
  getRealEstateLom4Tier: () => api.get('/api/lom/real-estate-4tier'),
  getSpecialtyChemicalsLom: () => api.get('/api/lom/specialty-chemicals'),
  getDownstreamLom: () => api.get('/api/lom/downstream'),
  getBasicChemicalsLom: () => api.get('/api/lom/basic-chemicals'),
  getRoadsHighwaysLom: () => api.get('/api/lom/roads-highways'),
  getRailsMetrosLom: () => api.get('/api/lom/rails-metros'),
  getDataCentersLom: () => api.get('/api/lom/data-centers'),
  getBridgesLom: () => api.get('/api/lom/bridges'),
  getConsumerProductsLom: () => api.get('/api/lom/consumer-products'),
  getDiscreteManufacturingLom: () => api.get('/api/lom/discrete-manufacturing'),
  getLifeSciencesLom: () => api.get('/api/lom/life-sciences'),
  getPulpPaperLom: () => api.get('/api/lom/pulp-paper'),
  getPortsLom: () => api.get('/api/lom/ports'),
  getShipbuildingLom: () => api.get('/api/lom/shipbuilding'),
  getMetalsLom: () => api.get('/api/lom/metals'),
  getMiningLom: () => api.get('/api/lom/mining'),
  getLngLom: () => api.get('/api/lom/lng'),
  getPipelineLom: () => api.get('/api/lom/pipeline'),
  getHydrogenLom: () => api.get('/api/lom/hydrogen'),
  getWaterTDLom: () => api.get('/api/lom/water-transmission-distribution'),
  getCoalPowerPlantLom: () => api.get('/api/lom/coal-power-plant'),
  getGasPowerPlantLom: () => api.get('/api/lom/gas-power-plant'),
  getNuclearPowerPlantLom: () => api.get('/api/lom/nuclear-power-plant'),
  getSolarPowerPlantLom: () => api.get('/api/lom/solar-power-plant'),
  getWindPowerPlantLom: () => api.get('/api/lom/wind-power-plant'),
  getCombinedCycleLom: () => api.get('/api/lom/combined-cycle-power-plant'),
  getPowerTDLom: () => api.get('/api/lom/power-transmission-distribution'),
  getBESSLom: () => api.get('/api/lom/battery-energy-storage'),
  getWaterDesalinationLom: () => api.get('/api/lom/water-desalination-plant'),
  getWastewaterRecyclingLom: () => api.get('/api/lom/wastewater-recycling-plant'),
  // Cross-LOM fuzzy search (indexes every L1/L2/L3/L4 across all 30+
  // industry LOMs + CRM 250 + HCM 227 BPs). See `/app/backend/lom_search.py`.
  search: (q, opts = {}) => api.get('/api/lom/search', { params: { q, limit: opts.limit || 60, level: opts.level || '', lom_id: opts.lomId || '' } }),
};

export const processAPI = {
  list: (params) => api.get('/api/processes', { params }),
  get: (id) => api.get(`/api/processes/${id}`),
  create: (data) => api.post('/api/processes', data),
  update: (id, data) => api.put(`/api/processes/${id}`, data),
  delete: (id) => api.delete(`/api/processes/${id}`),
  duplicate: (id) => api.post(`/api/processes/${id}/duplicate`),
  getRevisions: (id) => api.get(`/api/processes/${id}/revisions`),
  generateRaci: (id) => api.post(`/api/processes/${id}/raci`),
  simulate: (id) => api.get(`/api/processes/${id}/simulation`),
  saveVersion: (id, comment) => api.post(`/api/processes/${id}/version`, { comment }),
  getVersions: (id) => api.get(`/api/processes/${id}/versions`),
  getVersionDiff: (id, version) => api.get(`/api/processes/${id}/versions/${version}/diff`),
  restoreVersion: (id, version) => api.post(`/api/processes/${id}/versions/${version}/restore`),
  setHierarchy: (id, data) => api.put(`/api/processes/${id}/hierarchy`, data),
};

// Helper: poll AI task until completion, with retry on network errors
const pollAiTask = async (taskId, maxWaitMs = 300000) => {
  const startTime = Date.now();
  let consecutiveErrors = 0;
  while (Date.now() - startTime < maxWaitMs) {
    try {
      const { data } = await api.get(`/api/ai/task/${taskId}`);
      consecutiveErrors = 0;
      if (data.status === 'completed') return data.result;
      if (data.status === 'failed') throw new Error(data.error || 'AI task failed');
    } catch (err) {
      if (err.message && (err.message.includes('AI task failed') || err.message.includes('failed'))) throw err;
      consecutiveErrors++;
      if (consecutiveErrors >= 5) throw new Error('Network error while waiting for AI generation. Please try again.');
    }
    await new Promise(r => setTimeout(r, 4000));
  }
  throw new Error('AI generation timed out (5 min). Please try again.');
};

export const aiAPI = {
  generateProcess: async (prompt, context) => {
    const { data } = await api.post('/api/ai/generate-process', { prompt, context });
    if (data.taskId) {
      const result = await pollAiTask(data.taskId);
      return { data: result };
    }
    return { data };
  },
  chat: (message, processId, history) => api.post('/api/ai/chat', { message, processId, history }),
  reengineer: (processId, goals) => api.post('/api/ai/reengineer', { processId, goals }),
  analyzeUpload: (content, format) => api.post('/api/ai/analyze-upload', { content, format }),
  uploadAndAnalyze: (formData) => api.post('/api/ai/upload-and-analyze', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
};

export const paymentAPI = {
  getPricing: () => api.get('/api/payments/pricing'),
  buyTokens: (packageId) => api.post('/api/payments/buy-tokens', { packageId }),
  getBilling: () => api.get('/api/payments/billing'),
};

export const billingAPI = {
  // Public
  listPacks: () => api.get('/api/pricing/packs'),
  exchangeRate: () => api.get('/api/pricing/exchange-rate'),
  // Credits (auth)
  balance: () => api.get('/api/credits/balance'),
  transactions: (limit = 50) => api.get('/api/credits/transactions', { params: { limit } }),
  processAi: (processId) => api.get(`/api/credits/process/${processId}/ai`),
  topupAi: (processId) => api.post('/api/credits/topup-ai', { processId }),
  // Onboarding
  onboardingIndustries: () => api.get('/api/onboarding/industries'),
  onboardingStatus: () => api.get('/api/onboarding/status'),
  setIndustry: (industryId) => api.post('/api/onboarding/industry', { industryId }),
  // Payments
  createOrder: (packId, currency, billingDetails) => api.post('/api/payments/create-order', { packId, currency, billingDetails }),
  createCustomOrder: (customCredits, currency, billingDetails) => api.post('/api/payments/create-order', { customCredits, currency, billingDetails }),
  quoteCustom: (credits, currency) => api.post('/api/pricing/quote-custom', { credits, currency }),
  verifyPayment: (payload) => api.post('/api/payments/verify', payload),
  testFulfill: (razorpayOrderId) => api.post('/api/payments/test-fulfill', { razorpayOrderId }),
  // Invoices
  listInvoices: () => api.get('/api/invoices'),
  getInvoice: (id) => api.get(`/api/invoices/${id}`),
  invoicePdfUrl: (id) => `${API_URL}/api/invoices/${id}/pdf`,
};

export const exportAPI = {
  json: (id) => api.get(`/api/exports/${id}/json`),
  bpmn: (id) => api.get(`/api/exports/${id}/bpmn`, { responseType: 'blob' }),
  markdown: (id) => api.get(`/api/exports/${id}/markdown`, { responseType: 'blob' }),
  pdf: (id) => api.get(`/api/exports/${id}/pdf`, { responseType: 'arraybuffer' }),
};

export const collabAPI = {
  getUsers: (processId) => api.get(`/api/collaboration/${processId}/users`),
  invite: (processId, email, role) => api.post(`/api/collaboration/${processId}/invite`, { email, role }),
};

export const repositoryAPI = {};

export const brandingAPI = {
  get: () => api.get('/api/branding'),
  setDefault: (whitelabelDefault) => api.put('/api/branding', { whitelabelDefault }),
  setCoverPreset: (coverPreset) => api.put('/api/branding', { coverPreset }),
  uploadLogo: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/api/branding/logo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  removeLogo: () => api.delete('/api/branding/logo'),
  // ── Multi-element branding (logos + text lines with placements) ──
  addElement: ({ kind, label = '', placement = 'header-right', text = '', file = null }) => {
    const fd = new FormData();
    fd.append('kind', kind);
    fd.append('label', label);
    fd.append('placement', placement);
    if (kind === 'text') fd.append('text', text);
    if (kind === 'logo' && file) fd.append('file', file);
    return api.post('/api/branding/elements', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  updateElement: (elementId, patch) => api.patch(`/api/branding/elements/${elementId}`, patch),
  deleteElement: (elementId) => api.delete(`/api/branding/elements/${elementId}`),
  reorderElements: (ids) => api.post('/api/branding/elements/reorder', { ids }),
};

// AI-generated process definitions (used by the Report Builder "Process
// Definitions" sheet). Accepts a batch of BPs and returns one-paragraph
// definitions for each.
export const lomAiAPI = {
  bpDefinitions: (industry, bps) => api.post('/api/lom/bp-definitions', { industry, bps }),
  l1Details: (industry, domains) => api.post('/api/lom/l1-details', { industry, domains }),
};

// Combined Process Export API — bundles multiple saved processes into a single
// deliverable (Excel workbook or PDF booklet). The backend uses pypdf +
// reportlab to build the PDF, and openpyxl to build the Excel — one sheet per
// process with cover + TOC.
export const combinedExportAPI = {
  pdf: (processIds, title = null, includeDocumentation = true, includeSwimlane = true) =>
    api.post('/api/processes/combined-pdf', { processIds, title, includeDocumentation, includeSwimlane }, { responseType: 'blob', timeout: 180000 }),
  excel: (processIds, title = null) =>
    api.post('/api/processes/combined-excel', { processIds, title }, { responseType: 'blob', timeout: 90000 }),
  reengAudit: (structureId) =>
    api.get('/api/reeng/combined-audit', { params: { structureId } }),
  reengPdf: (structureId, procIds, opts = {}) =>
    api.post('/api/reeng/combined-pdf', {
      structureId, procIds,
      title: opts.title || null,
      includeAsIs: opts.includeAsIs !== false,
      includeToBe: opts.includeToBe !== false,
      includeDelta: opts.includeDelta !== false,
    }, { responseType: 'blob', timeout: 180000 }),
};

export const notificationsAPI = {
  list: () => api.get('/api/notifications'),
  markRead: (ids) => api.post('/api/notifications/mark-read', { ids }),
  markAllRead: () => api.post('/api/notifications/mark-read', { all: true }),
  getPrefs: () => api.get('/api/auth/me/notification-prefs'),
  updatePrefs: (prefs) => api.put('/api/auth/me/notification-prefs', { prefs }),
  getMarketingPrefs: () => api.get('/api/auth/me/marketing-prefs'),
  updateMarketingPrefs: (marketingOptIn) => api.put('/api/auth/me/marketing-prefs', { marketingOptIn }),
};

export const userAPI = {
  getDashboard: () => api.get('/api/users/dashboard'),
  updateProfile: (data) => api.put('/api/users/profile', data),
};

export default api;
